import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, Clock, Users, AlertCircle } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
import { TestimonialsSlider } from '../components/TestimonialsSlider';
import { ProcessSlider } from '../components/ProcessSlider';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useLeadForm } from '../hooks/useLeadForm';
import { getDialCodeFromCountry, getCountryCodesForSelect } from '../utils/countryMapping';
import { openZendeskChat } from '../utils/zendesk';

export const Home = ({ onOpenPopup }) => {
  const { geolocationData, isLoading, error, submitForm, clearError } = useLeadForm();
  const [heroFormData, setHeroFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    contactNumber: '',
    message: ''
  });

  const countryCodesOptions = getCountryCodesForSelect();

  // Auto-populate country from geolocation
  useEffect(() => {
    if (geolocationData?.data?.countryCode) {
      const countryCode = geolocationData.data.countryCode;
      const dialCode = getDialCodeFromCountry(countryCode);
      setHeroFormData(prev => ({
        ...prev,
        country: countryCode,
        countryCode: `+${dialCode}`
      }));
    }
  }, [geolocationData]);

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault();
    clearError();
    
    await submitForm(heroFormData);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-600 via-orange-500 to-red-600 text-white py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NjQzNjMyNDF8MA&ixlib=rb-4.1.0&q=85"
            alt="Professional business background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600/75 via-orange-500/75 to-red-600/80"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Protect Your Brand with Professional Trademark Services
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-orange-100">
                Secure your business identity with expert trademark registration and comprehensive legal protection across the United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={onOpenPopup}
                  className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 font-semibold"
                >
                  Register My Trademark
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={openZendeskChat}
                  className="js-chat border-2 border-white text-white hover:bg-white hover:text-orange-600 text-lg px-8 py-6 font-semibold"
                >
                  Chat Now
                </Button>
              </div>
            </div>

            {/* Right Side - Form */}
            <div>
              <Card className="p-8 shadow-2xl bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Get Started Today</h2>
                <p className="text-gray-600 mb-6">Fill out the form below and we'll contact you within 24 hours</p>
                
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleHeroFormSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="hero-name" className="text-gray-700">Name *</Label>
                    <Input
                      id="hero-name"
                      type="text"
                      required
                      value={heroFormData.name}
                      onChange={(e) => setHeroFormData({ ...heroFormData, name: e.target.value })}
                      className="mt-1"
                      placeholder="Enter your full name"
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="hero-email" className="text-gray-700">Email *</Label>
                    <Input
                      id="hero-email"
                      type="email"
                      required
                      value={heroFormData.email}
                      onChange={(e) => setHeroFormData({ ...heroFormData, email: e.target.value })}
                      className="mt-1"
                      placeholder="Enter your email"
                      disabled={isLoading}
                    />
                  </div>

                  {geolocationData?.data?.countryName && (
                    <div>
                      <Label className="text-gray-700">Country</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-gray-900 font-medium">{geolocationData.data.countryName}</p>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="hero-contact" className="text-gray-700">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={heroFormData.countryCode}
                        onValueChange={(value) => {
                          const selectedCountry = countryCodesOptions.find(c => c.code === value);
                          setHeroFormData({ 
                            ...heroFormData, 
                            countryCode: value,
                            country: selectedCountry?.countryCode || 'US'
                          });
                        }}
                        disabled={isLoading}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodesOptions.map((item) => (
                            <SelectItem key={item.code} value={item.code}>
                              {item.code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="hero-contact"
                        type="tel"
                        required
                        value={heroFormData.contactNumber}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value.length <= 15) {
                            setHeroFormData({ ...heroFormData, contactNumber: value });
                          }
                        }}
                        placeholder="Phone number"
                        className="flex-1"
                        maxLength={15}
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="hero-message" className="text-gray-700">Message</Label>
                    <Textarea
                      id="hero-message"
                      value={heroFormData.message}
                      onChange={(e) => setHeroFormData({ ...heroFormData, message: e.target.value })}
                      className="mt-1"
                      placeholder="Tell us about your trademark needs..."
                      rows={4}
                      disabled={isLoading}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg font-semibold"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Image - Equal Width */}
            <div className="order-2 lg:order-1 w-full">
              <img
                src="https://images.unsplash.com/photo-1562564055-71e051d33c19?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjb25zdWx0aW5nJTIwZG9jdW1lbnRzfGVufDB8fHx8MTc2NDM2NDQwNnww&ixlib=rb-4.1.0&q=85"
                alt="Professional trademark consultation"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>

            {/* Content - Equal Width */}
            <div className="order-1 lg:order-2 w-full">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Protecting Your Brand Identity Has Never Been Easier
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                At Corporate Marking, we understand that your brand is your business's most valuable asset. Our comprehensive trademark services provide the legal protection you need to safeguard your intellectual property.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                With over 15 years of experience and a 98% success rate, our licensed trademark attorneys have helped thousands of businesses secure federal trademark protection.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Expert Legal Guidance</h3>
                    <p className="text-gray-600">Licensed attorneys guide you through every step of the process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Comprehensive Protection</h3>
                    <p className="text-gray-600">Federal trademark protection with ongoing monitoring services.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Transparent Pricing</h3>
                    <p className="text-gray-600">Clear, affordable pricing packages starting at just $45.</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={onOpenPopup}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold"
                >
                  Register My Trademark
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={openZendeskChat}
                  className="js-chat border-2 border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-6 text-lg font-semibold"
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">10,000+</div>
              <div className="text-gray-600">Trademarks Registered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">98%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">15+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">24/7</div>
              <div className="text-gray-600">Client Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Corporate Marking?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We provide comprehensive trademark services tailored to protect your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Protection</h3>
              <p className="text-gray-600">
                Full-service trademark protection with ongoing monitoring and support
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Award className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Legal Team</h3>
              <p className="text-gray-600">
                Experienced attorneys specialized in intellectual property law
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Clock className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Processing</h3>
              <p className="text-gray-600">
                Quick turnaround times with expedited filing options available
              </p>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-shadow">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-100 text-orange-600 mb-4">
                <Users className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-600">
                Personal case manager assigned to guide you through every step
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Slider */}
      <ProcessSlider />

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Left Side - FAQ Content */}
            <div className="order-2 lg:order-1 w-full">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Get answers to the most common questions about our trademark registration services
              </p>

              <Accordion type="single" collapsible className="space-y-4">
                <AccordionItem value="item-1" className="bg-white border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                      <span>How long does trademark registration take?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pl-11 pt-2">
                    The typical timeline is 8-12 months from filing to registration. This varies based on USPTO workload and whether any office actions are issued. With our expedited service, we can help speed up the initial filing process.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="bg-white border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                      <span>What can I trademark?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pl-11 pt-2">
                    You can trademark business names, logos, slogans, product names, service names, and even certain sounds, colors, or designs that are distinctly associated with your brand. Our team will help you determine what aspects of your brand are trademarkable.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="bg-white border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                      <span>Do I need a lawyer for trademark registration?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pl-11 pt-2">
                    While not legally required, working with trademark attorneys significantly increases your approval chances and ensures proper protection. Our experienced attorneys handle complex searches, proper classifications, and respond to USPTO office actions on your behalf.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="bg-white border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                      <span>How much does trademark registration cost?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pl-11 pt-2">
                    Our services start at $45 plus USPTO filing fees ($250-$350 per class). We offer transparent pricing with no hidden costs. Our packages range from basic filing to comprehensive protection with monitoring and dedicated case managers.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5" className="bg-white border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                      <span>What's included in your trademark search?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pl-11 pt-2">
                    We conduct comprehensive searches of federal, state, and common law databases to identify potential conflicts before filing. Our search includes phonetic similarities, visual similarities, and conceptual similarities across all relevant trademark classes.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-6" className="bg-white border border-gray-200 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-orange-600">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                      <span>Can I trademark a name similar to another business?</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pl-11 pt-2">
                    It depends on several factors including the industry, how similar the marks are, and whether confusion is likely. Our attorneys evaluate each case individually and provide expert guidance on your likelihood of success before you invest in the application process.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              <div className="mt-8">
                <Button
                  onClick={onOpenPopup}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg font-semibold"
                >
                  Have More Questions? Contact Us
                </Button>
              </div>
            </div>

            {/* Right Side - Image */}
            <div className="order-1 lg:order-2 w-full">
              <img
                src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMGNvbnN1bHRhdGlvbnxlbnwwfHx8fDE3NjQzNjYxNjF8MA&ixlib=rb-4.1.0&q=85"
                alt="Professional consultation and support"
                className="w-full h-full object-cover rounded-2xl shadow-2xl min-h-[600px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Protect Your Brand?</h2>
          <p className="text-xl mb-8 text-orange-100 max-w-2xl mx-auto">
            Join thousands of satisfied clients who trust us with their trademark protection. Get started today!
          </p>
          <Button
            size="lg"
            onClick={onOpenPopup}
            className="bg-white text-orange-600 hover:bg-orange-50 text-lg px-8 py-6 font-semibold"
          >
            Register Your Trademark Now
          </Button>
        </div>
      </section>
    </div>
  );
};